import { Menu, Transition } from "@headlessui/react"
import { EnvelopeIcon, PhoneIcon, QuestionMarkCircleIcon, VideoCameraIcon } from "@heroicons/react/24/outline"
import { QuestionMarkCircleIcon as QuestionMarkCircleIconSolid } from "@heroicons/react/20/solid"
import { Fragment, useContext, useEffect, useState } from "react"
import Button from "../ui/Button"
import CopyPasteButton from "../ui/CopyPasteButton"
import { SparklesIcon } from "@heroicons/react/16/solid"
import { XMarkIcon } from "@heroicons/react/24/solid"
import parkIconPath from "../../../assets/images/park_icon__green.svg"
import { useCamp } from "~/api2/hooks/useCamp"
import { useMe } from "~/api2/hooks/useMe"
import useGoogleAnalytics from "~/hooks/useGoogleAnalytics"

const HelpPopover = ({ currentPage }) => {
  const [isOpen, setIsOpen] = useState(false)
  const {
    index: { data: camp },
  } = useCamp()
  const { data: user } = useMe()
  
  const { trackForPark } = useGoogleAnalytics(camp)
  // Add an array of article options
  const articleOptions = [
    { page: "reservations", articleName: "How to Create a Reservation", helpkitId: "4nD3LrqbXgvZKLk6NQ3kjM" },
    {
      page: "to-dos",
      articleName: "How to use To-do's",
      helpkitId: "3xGPjnoXwWKPw733N8Trnk",
    },
    {
      page: "reports",
      articleName: "How to use Reports",
      helpkitId: "4AaZYugeoSSjjRuump4BcC",
    },
    // {
    //   page: "invoices",
    //   articleName: "How to use Invoices to manage your customers",
    //   helpkitId: "4AaZYugeoTqKjAVSv7KCQ8",
    // },
    {
      page: "store",
      articleName: "How to us the Store",
      helpkitId: "4AaZYugeoTqKjAVSv7KCQ8",
    },
    {
      page: "guests",
      articleName: "How to use Guests",
      helpkitId: "4AaZYugeoRRSeYAhtVVStM",
    },

    // {
    //   page: "settings",
    //   articleName: "Setting up your Campground",
    //   helpkitId: "4AaZYugeoTqKjAVSv7KCQ8",
    // },
  ]

  const matchedArticle = articleOptions.find((opt) => opt.page === currentPage)

  return (
    <div className="fixed bottom-2 sm:bottom-4 right-2 sm:right-4 z-30">
      <Menu as="div" className="relative inline-block text-left" data-testid="nav-bar-admin-dropdown-menu">
        <div>
          <Menu.Button
            data-testid="help-popover-button"
            className="bg-blue-500 hover:bg-blue-600 hidden sm:flex items-center justify-center text-white w-24 px-2.5 py-2 rounded-full"
            onClick={() => {
              setIsOpen(!isOpen)
              trackForPark("click_help_button_" + currentPage, {
                camp: camp.name
              })
            }}
          >
            <QuestionMarkCircleIcon className="w-5.5 h-5.5 mr-1" />
            <span className="text-lg font-medium mr-0.5">Help</span>
          </Menu.Button>
          <Menu.Button
            data-testid="help-popover-button"
            className="bg-blue-500 hover:bg-blue-600 flex sm:hidden items-center justify-center text-white p-1.5 rounded-full"
            onClick={() => setIsOpen(!isOpen)}
          >
            <QuestionMarkCircleIcon className="w-6 h-6" />
          </Menu.Button>
        </div>

        <Transition
          as={Fragment}
          show={isOpen}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <Menu.Items className="absolute -right-2 sm:right-0 bottom-10 sm:bottom-12 z-50 w-screen sm:w-[400px] h-[calc(100vh-42px)] sm:h-[calc(100vh-84px)] overflow-y-auto p-4 rounded-md bg-white shadow-xl ring-1 ring-black ring-opacity-5 focus:outline-none">
            <div className="flex items-end justify-between mt-1 mb-4 pb-4 border-b border-gray-200">
              <div className="flex items-end">
                <div className="flex-shrink-0 mr-3">
                  <img data-testid="nav-bar-logo" src={parkIconPath} className="block z-10 h-8 w-auto" />
                </div>
                <div className="text-2xl font-bold mb-0.5 text-[#43624C]" style={{ fontFamily: "Lora" }}>
                  Park Support
                </div>
              </div>
              <div className={"absolute top-0 right-0 pt-4 pr-8 block z-20"}>
                <button
                  type="button"
                  className="rounded-lg bg-white hover:bg-gray-200 p-1.5 focus:outline-none"
                  onClick={() => setIsOpen(false)}
                >
                  <span className="sr-only">Close</span>
                  <XMarkIcon className="w-8 h-8" aria-hidden="true" />
                </button>
              </div>
            </div>
            <div className="">
              {matchedArticle && (
                <div className="bg-yellow-100 border-l-4 border-yellow-400 p-3 rounded mb-4 shadow-sm">
                  <p className="font-semibold text-yellow-900 mb-2">
                    It looks like you're on the {currentPage} page, would this article help?
                  </p>
                  <Button
                    variant="white"
                    onClick={() => {
                      if (window.Helpkit) {
                        window.Helpkit.show("popover", "article", matchedArticle.helpkitId)
                      } else {
                        console.warn("Helpkit is not loaded yet.")
                      }
                    }}
                    className="flex items-center rounded-md hover:bg-gray-50  px-3 py-1.5"
                  >
                    <SparklesIcon className="w-4 h-4 text-yellow-500 animate-pulse flex-shrink-0" />
                    <span className="text-md font-medium ml-2 text-blue-500">{matchedArticle.articleName}</span>
                  </Button>
                </div>
              )}
              <div className="mb-8">
                <div className="mb-2 font-bold">Help Center</div>
                <div className="mb-3">
                  The help center contains a variety of tutorial articles and videos to help you use Park
                </div>
                <Button
                  variant="white"
                  className="text-blue-500"
                  onClick={() => {
                    if (window.Helpkit) {
                      window.Helpkit.show("popover")
                    } else {
                      console.warn("Helpkit is not loaded yet.")
                    }
                  }}
                  icon={<QuestionMarkCircleIconSolid className="w-5 h-5 text-blue-500" />}
                >
                  Help Center
                </Button>
              </div>

              <div className="mb-8">
                <div className="mb-2 font-bold">Submit a support ticket</div>
                <div className="mb-3">
                  If you can't find an answer to your question or need to report a bug, submit a ticket to notify our
                  team
                </div>
                <Button
                  variant="white"
                  onClick={() => {
                    if (window.Helpkit) {
                      window.Helpkit.setContactFields({
                        name: camp?.name,
                        email: user?.email,
                        metadata: `Camp Name: ${camp?.name}`,
                      })
                      window.Helpkit.show("popover", "contact")
                    } else {
                      console.warn("Helpkit is not loaded yet.")
                    }
                  }}
                  icon={<EnvelopeIcon className="w-5 h-5 text-gray-700" />}
                >
                  Submit a ticket
                </Button>
              </div>

              {camp?.is_phone_support_offered && (
                <div className="mb-8">
                  <div className="mb-2 font-bold">Live Support</div>
                  <div className="mb-3">
                    If you have an urgent question you'd like to discuss in real time, give us a call or schedule a web
                    meeting
                  </div>
                  <div className="flex items-center gap-x-3">
                    <Button
                      variant="white"
                      className="flex-1 flex-nowrap"
                      onClick={() => {
                        window.location.href = "tel:(802) 802-8031"
                      }}
                      icon={<PhoneIcon className="w-5 h-5 text-gray-700" />}
                    >
                      <span className="hidden sm:block mr-1"> </span>(802) 802-8031
                    </Button>
                    <Button
                      variant="white"
                      className="flex-1"
                      onClick={() => {
                        window.open("https://calendly.com/d/cmzy-94f-rz3/park-support-call", "_blank")
                      }}
                      icon={<VideoCameraIcon className="w-5 h-5 text-gray-700" />}
                    >
                      Schedule{"  "}
                      <span className="hidden sm:block ml-1">Meeting</span>
                    </Button>
                  </div>
                </div>
              )}

              <div className="mb-4">
                <div className="flex">
                  <div className="mb-2 font-bold mr-1.5">Refer and earn</div>
                  <SparklesIcon className="text-yellow-500 h-4.5 w-4.5" />
                </div>
                <div className="mb-3">
                  If you know any other campground operators, spread the word and we'll send you $250 when they go live!
                </div>

                <div className="w-[14rem]">
                  <CopyPasteButton
                    text={"your referral link"}
                    value={`https://parkwith.us?utm_source=${user?.id}`}
                    buttonClassName="h-10.5 w-full"
                  />
                </div>
              </div>
            </div>
          </Menu.Items>
        </Transition>
      </Menu>
    </div>
  )
}

export default HelpPopover
